import { useEffect, useState } from 'react';
import {
  getWeeklyPlan,
  updatePlanCompletion,
  closeWeeklyPlan,
} from '../api/core.js';
import { usePolicy } from '../components/PolicyContext.jsx';

// Goals view. Shows the makler's current weekly plan: their targets and how
// much has been completed so far, plus a traffic-light status. Closing the
// week is a JCH-level action, exposed here for completeness.
export default function Goals() {
  const { policy } = usePolicy();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const row = await getWeeklyPlan();
      setPlan(row);
    } catch (err) {
      setError(err.message || 'Cíle se nepodarliločitít');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clamp = (value) => {
    const n = Number(value) || 0;
    return n < 0 ? 0 : n > 100 ? 100 : n;
  };

  const handleComplete = async (metric) => {
    if (!plan) return;
    const completed = clamp(plan[`${metric}_completed`]);
    const goal = plan[`target_${metric}`];
    try {
      const updated = await updatePlanCompletion({ completed, goal });
      setPlan(updated);
    } catch (err) {
      setError(err.message || 'Pokrok se nepodarlilo ulanit');
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Uzavit týdenní plán?')) return;
    try {
      await closeWeeklyPlan();
      await load();
    } catch (err) {
      setError(err.message || 'Plán se nepodarlilo navit');
    }
  };

  if (loading) {
    return <section className="dashboard"><p className="dashboard__status">Načítá se…</p></section>;
  }

  if (!plan) {
    return (
      <section className="dashboard">
        <h1 className="dashboard__title">Mé cíle</h1>
        {error && <p className="dashboard__error" role="alert">{error}</p>}
        <p className="dashboard__status">Zatím žádný aktivní plán na tento týden.</p>
      </section>
    );
  }

  const targetMetric = (label, key) => {
    const completed = plan[`${key}_completed`] ?? 0;
    const goal = plan[`target_${key}`] ?? 0;
    const pct = goal > 0 ? Math.min(100, Math.round((completed / goal) * 100)) : 0;
    return (
      <li key={key} className="goals__row">
        <div className="goals__label">
          <span className="goals__metric">{label}</span>
          <span className="goals__target">{completed} / {goal}</span>
        </div>
        <div className="goals__bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div
            className="goals__bar-fill"
            style={{ width: `${pct}%`, background: goal > 0 ? pctColor(pct, policy.trafficLight) : 'var(--cs-data-track)' }}
          />
        </div>
        <button
          className="button button--secondary"
          onClick={() => handleComplete(key)}
          aria-label={`Oznámit dokončení ${label}`}
        >
          Oznámit
        </button>
      </li>
    );
  };

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <h1 className="dashboard__title">Mé cíle</h1>
      </div>

      {error && <p className="dashboard__error" role="alert">{error}</p>}

      <p className="dashboard__lead">
        Prohlédněte si týdenní cíle. Oznámte svůj pokrok každý večer.
      </p>

      <article className="card">
        <div className="goals__header">
          <h2 className="card__title">Týdní plán</h2>
          {plan.traffic_light_color && (
            <span className={`goals__light goals__light--${plan.traffic_light_color}`}>
              {plan.traffic_light_color === 'green' ? 'Zelená' :
               plan.traffic_light_color === 'orange' ? 'Oranžová' : 'Červená'}
            </span>
          )}
        </div>

        <ul className="goals__list">
          {targetMetric('Kontakty', 'contacts')}
          {targetMetric('Schůzky', 'meetings')}
          {targetMetric('Akvizice', 'acquisitions')}
        </ul>
      </article>

      {plan.week_closed ? (
        <p className="dashboard__status">Týdní plán je uzaven.</p>
      ) : (
        <button className="button button--quiet" onClick={handleClose}>
          Uavit plán
        </button>
      )}
    </section>
  );
}

// pctColor maps a completion percentage to a traffic-light color, matching the
// backend's traffic_light_color column. Never decorative — it reports progress.
function pctColor(pct, thresholds) {
  if (pct >= thresholds.greenFrom) return 'var(--cs-level-leading)';
  if (pct >= thresholds.orangeFrom) return 'var(--cs-level-developing)';
  return 'var(--cs-level-beginner)';
}
